import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { LESSON_KINDS } from '../constants';
import { useCreateLesson } from '../queries';

const schema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(255),
  kind: z.enum(['material', 'guide', 'video', 'link']),
  order: z.string().refine((v) => Number.isInteger(Number(v)) && Number(v) >= 0, 'Whole number'),
  content: z.string(),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = ['title', 'kind', 'order', 'content'] as const;

export function AddLessonForm({ courseId, nextOrder }: { courseId: number; nextOrder: number }) {
  const create = useCreateLesson(courseId);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: '', kind: 'material', order: String(nextOrder), content: '' },
  });

  async function handleSubmit(values: FormValues) {
    try {
      await create.mutateAsync({
        title: values.title,
        kind: values.kind,
        order: Number(values.order),
        content: values.content,
      });
      form.reset({ title: '', kind: values.kind, order: String(Number(values.order) + 1), content: '' });
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-[1fr_8rem_6rem]">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Welcome & tools" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="kind"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Kind</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {LESSON_KINDS.map((k) => (
                      <SelectItem key={k.value} value={k.value}>
                        {k.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="order"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Order</FormLabel>
                <FormControl>
                  <Input type="number" min={0} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Content</FormLabel>
              <FormControl>
                <Textarea rows={2} placeholder="Lesson body or a link…" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}
        <Button type="submit" size="sm" disabled={create.isPending}>
          <Plus /> {create.isPending ? 'Adding…' : 'Add lesson'}
        </Button>
      </form>
    </Form>
  );
}
